import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Icon,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  SimpleGrid,
  Text,
  VStack,
} from '@chakra-ui/react';
import { format, startOfDay } from 'date-fns';
import enLocale from 'date-fns/locale/en-US';
import esLocale from 'date-fns/locale/es';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarIcon, ClockIcon, HashIcon, MapPinIcon, UsersIcon } from 'lucide-react';
import { Company, Reservation } from '~/core/types';
import { useAuth } from '~/hooks/useAuth';
import AgregarACalendario from '~/pages/public/Forms/Reservation/calendario/AgregarACalendario';
import { hora12, lineaDeFecha } from './crearSerie';

/**
 * La tarjeta de confirmación de una SERIE, para hacerle captura de pantalla y
 * mandársela al cliente. Es hermana de la tarjeta de una reserva suelta
 * (`ReservationSummary`): misma foto, mismo azul, mismo logo, letra algo más
 * pequeña porque hay más que contar.
 *
 * Lo importante es que el mensaje diga **exactamente cuándo tiene reservas**
 * el cliente, sin confundir. Una serie deja de ser "todos los jueves" en
 * cuanto se quita una fecha al crearla, se cancela una por el camino, se
 * mueve una de sala o de hora, o ya pasaron algunas. Por eso aquí nunca se
 * usa la frase de la serie ("los jueves del 1 al 29"): se listan las fechas
 * reales, una a una, y se cuenta aparte lo que no entra.
 *
 * Reglas del texto (todas probadas):
 *  · Solo se listan las fechas **vigentes y por venir** (de hoy en adelante,
 *    no canceladas). Es lo que el cliente necesita saber.
 *  · Sala, horario y personas van **una sola vez arriba si son iguales en
 *    todas**; si alguna fecha difiere, cada fecha lleva lo suyo.
 *  · Hasta 6 fechas: una línea por fecha con día, fecha, hora y código.
 *    Más de 6: fechas agrupadas por mes en columnas, más compactas, y un
 *    solo código abajo (cualquier código de la serie abre todas las fechas).
 *  · Las canceladas y las ya realizadas se nombran en una nota pequeña,
 *    para que quede claro que no se olvidaron: simplemente no entran.
 *  · Si ya pasaron todas, se muestran como "Tus reservas fueron"; si están
 *    todas canceladas, se dice y no se lista nada.
 */

export type FechaDeSerie = {
  id: string;
  code: string;
  cuando: Date | null;
  sala: string;
  horaInicio: string;
  /** Vacía cuando la reserva no tiene hora de fin. */
  horaFin: string;
  estado: string;
  personas?: number;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  /** La reserva desde la que se abrió: nombre, idioma y datos para el calendario. */
  reserva: Record<string, any> | null | undefined;
  fechas: FechaDeSerie[];
};

type Idioma = 'es' | 'en';

const FOTO =
  "url('https://firebasestorage.googleapis.com/v0/b/sontickets-1e1a6/o/noi.jpg?alt=media&token=98f6c083-8768-4dc0-8f26-ef2a6d5eea45')";
const LOGO =
  'https://s1qvhykx7kzcxtf4.public.blob.vercel-storage.com/news/logo-oficial-noi-sontickets.svg';
const AZUL = '#0072ced9';

const nombrePropio = (nombre: string) =>
  nombre
    .split(' ')
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase())
    .join(' ');

/** "8 am – 6 pm", "a las 8 am" (sin fin) en el idioma que toque. */
const horario = (f: FechaDeSerie, idioma: Idioma) => {
  if (!f.horaInicio) return '';
  if (f.horaFin) return `${hora12(f.horaInicio)} – ${hora12(f.horaFin)}`;
  return idioma === 'en' ? `at ${hora12(f.horaInicio)}` : `a las ${hora12(f.horaInicio)}`;
};

/** "Jueves 1 de octubre" / "Thursday, October 1"; con el año si no es el actual. */
const diaLargo = (d: Date, idioma: Idioma) => {
  const conAnio = d.getFullYear() !== new Date().getFullYear();
  const texto =
    idioma === 'en'
      ? format(d, conAnio ? 'EEEE, MMMM d, yyyy' : 'EEEE, MMMM d', { locale: enLocale })
      : format(d, conAnio ? "EEEE d 'de' MMMM 'de' yyyy" : "EEEE d 'de' MMMM", {
          locale: esLocale,
        });
  return texto.charAt(0).toUpperCase() + texto.slice(1);
};

/** "Jue 1" / "Thu 1", para las cuadrículas con muchas fechas. */
const diaCorto = (d: Date, idioma: Idioma) => {
  const texto = format(d, idioma === 'en' ? 'EEE d' : 'EEE d', {
    locale: idioma === 'en' ? enLocale : esLocale,
  }).replace('.', '');
  return texto.charAt(0).toUpperCase() + texto.slice(1);
};

/** "Octubre de 2026" / "October 2026". */
const mesLargo = (d: Date, idioma: Idioma) => {
  const texto =
    idioma === 'en'
      ? format(d, 'MMMM yyyy', { locale: enLocale })
      : format(d, "MMMM 'de' yyyy", { locale: esLocale });
  return texto.charAt(0).toUpperCase() + texto.slice(1);
};

/** "jueves 8 y jueves 22 de octubre" para las notas; corto y sin repetir el mes si coincide. */
const enumerar = (fechas: Date[], idioma: Idioma) => {
  const partes = fechas.map((d) =>
    idioma === 'en'
      ? format(d, 'EEE, MMM d', { locale: enLocale })
      : format(d, "EEE d 'de' MMM", { locale: esLocale }).replace(/\./g, '')
  );
  if (partes.length <= 1) return partes[0] ?? '';
  const y = idioma === 'en' ? 'and' : 'y';
  return `${partes.slice(0, -1).join(', ')} ${y} ${partes[partes.length - 1]}`;
};

const unico = <T,>(valores: T[]): T | undefined =>
  new Set(valores.map((v) => JSON.stringify(v))).size === 1 ? valores[0] : undefined;

/**
 * El cuerpo de la tarjeta, separado de la ventana para poder probar el texto
 * sin navegador. `hoy` se puede fijar desde fuera por lo mismo.
 */
export const CuerpoTarjetaSerie = ({
  reserva,
  fechas,
  empresa,
  hoy: hoyFijado,
}: {
  reserva: Record<string, any> | null | undefined;
  fechas: FechaDeSerie[];
  empresa?: Company;
  hoy?: Date;
}) => {
  const { i18n } = useTranslation();
  const idioma: Idioma = reserva?.lang === 'en' ? 'en' : 'es';
  const t = i18n.getFixedT(idioma);

  const hoy = startOfDay(hoyFijado ?? new Date()).getTime();

  const datos = useMemo(() => {
    const conFecha = fechas.filter((f) => f.cuando);
    const vigentes = conFecha.filter((f) => f.estado !== 'cancelled');
    const canceladas = conFecha.filter((f) => f.estado === 'cancelled');
    const porVenir = vigentes.filter((f) => startOfDay(f.cuando as Date).getTime() >= hoy);
    const pasadas = vigentes.filter((f) => startOfDay(f.cuando as Date).getTime() < hoy);
    // Lo que se lista: lo que viene; si ya no viene nada, lo que fue.
    const lista = porVenir.length > 0 ? porVenir : pasadas;
    return {
      lista,
      porVenir,
      pasadas,
      canceladas,
      salaComun: unico(lista.map((f) => f.sala)),
      horarioComun: unico(lista.map((f) => horario(f, idioma))),
      personasComun: unico(lista.map((f) => f.personas ?? reserva?.numberPeople ?? null)),
      compacta: lista.length > 6,
    };
  }, [fechas, hoy, idioma, reserva?.numberPeople]);

  const { lista, porVenir, pasadas, canceladas, salaComun, horarioComun, personasComun, compacta } =
    datos;
  // Con pocas fechas cada línea dice su hora (día, fecha y hora, siempre), así
  // que arriba no se repite; con muchas, la hora común va arriba una vez.
  const horarioArriba = compacta ? horarioComun : undefined;

  const nombre = nombrePropio(reserva?.namesAndSurnames ?? reserva?.name ?? '');
  const titulo =
    lista.length === 0
      ? t('reserve_confirmation.series_title_none')
      : porVenir.length === 0
      ? t('reserve_confirmation.series_title_past')
      : lista.length === 1
      ? t('reserve_confirmation.series_title_one')
      : t('reserve_confirmation.series_title_many', { count: lista.length });
  const personasTexto = (n: number | null | undefined) =>
    !n
      ? ''
      : n === 1
      ? t('reserve_confirmation.series_people_one')
      : t('reserve_confirmation.series_people_many', { count: n });
  const codigoPrincipal = lista[0]?.code ?? fechas.find((f) => f.code)?.code ?? '';

  // Meses, para la vista compacta.
  const porMes = useMemo(() => {
    const grupos: { clave: string; titulo: string; fechas: FechaDeSerie[] }[] = [];
    lista.forEach((f) => {
      const d = f.cuando as Date;
      const clave = `${d.getFullYear()}-${d.getMonth()}`;
      let g = grupos.find((x) => x.clave === clave);
      if (!g) {
        g = { clave, titulo: mesLargo(d, idioma), fechas: [] };
        grupos.push(g);
      }
      g.fechas.push(f);
    });
    return grupos;
  }, [lista, idioma]);

  const compartir = () => {
    const nombreEmpresa = empresa?.name ?? 'Noi Coworking';
    const lineas = lista.map((f) =>
      lineaDeFecha({
        fecha: f.cuando as Date,
        horaInicio: f.horaInicio,
        horaFin: f.horaFin || null,
        sala: f.sala,
        diaCompleto: false,
        ingles: idioma === 'en',
      })
    );
    const mensaje = [
      idioma === 'en' ? `Hi, *${nombre}*!` : `¡Hola, *${nombre}*!`,
      `*${t('reserve_confirmation.series_share_intro', { company: nombreEmpresa })}*`,
      '',
      ...lineas.map((l) => `• ${l}`),
      '',
      codigoPrincipal ? t('reserve_confirmation.series_code_hint', { code: codigoPrincipal }) : '',
      'https://noi.work/',
    ].join('\n');
    window.open(`https://wa.me/?text=${encodeURIComponent(mensaje)}`, '_blank');
  };

  // Con muchas fechas el panel azul se ensancha y la foto queda de fondo.
  const anchoPanel = compacta
    ? { base: '100%', md: lista.length > 16 ? '100%' : '72%' }
    : { base: '100%', md: '58%' };
  const columnas =
    lista.length > 16
      ? { base: 2, md: 4 }
      : lista.length > 10
      ? { base: 2, md: 3 }
      : { base: 1, md: 2 };

  return (
    <Flex
      direction={{ base: 'column', md: 'row' }}
      w='full'
      backgroundImage={FOTO}
      backgroundSize='cover'
      backgroundRepeat='no-repeat'
      backgroundPosition='right'
      overflow='hidden'
      boxShadow='2xl'
      rounded='2xl'
      minH='420px'
    >
      <Box w={anchoPanel} bg={AZUL} color='white' px={{ base: 6, md: 10 }} py={{ base: 8, md: 8 }}>
        <VStack spacing={5} align='stretch'>
          <Image src={LOGO} alt='NOI Coworking' width={150} />

          <Box>
            <Heading size='lg' fontWeight='extrabold' letterSpacing='tight' lineHeight='1.1'>
              {t('reserve_confirmation.title_chunk_1')} {nombre}.
            </Heading>
            <Text fontSize='lg' fontWeight='semibold' mt={2} data-testid='serie-titulo'>
              {titulo}
            </Text>
          </Box>

          {/* Lo que comparten todas las fechas, dicho una sola vez. */}
          {lista.length > 0 && (salaComun || horarioArriba || personasComun) && (
            <HStack
              spacing={4}
              flexWrap='wrap'
              fontSize='md'
              fontWeight='medium'
              data-testid='serie-comun'
            >
              {salaComun && (
                <HStack spacing={1.5}>
                  <Icon as={MapPinIcon as any} boxSize={4} />
                  <Text>{salaComun}</Text>
                </HStack>
              )}
              {horarioArriba && (
                <HStack spacing={1.5}>
                  <Icon as={ClockIcon as any} boxSize={4} />
                  <Text>{horarioArriba}</Text>
                </HStack>
              )}
              {personasComun ? (
                <HStack spacing={1.5}>
                  <Icon as={UsersIcon as any} boxSize={4} />
                  <Text>{personasTexto(personasComun)}</Text>
                </HStack>
              ) : null}
            </HStack>
          )}

          {/* Las fechas, una a una. */}
          {lista.length > 0 && !compacta && (
            <VStack align='stretch' spacing={1.5} data-testid='serie-fechas'>
              {lista.map((f) => (
                <Flex
                  key={f.id}
                  align='center'
                  gap={3}
                  bg='whiteAlpha.200'
                  borderRadius='md'
                  px={3}
                  py={2}
                >
                  <Icon as={CalendarIcon as any} boxSize={4} flexShrink={0} />
                  <Box flex={1} minW={0}>
                    <Text fontSize='md' fontWeight='semibold' noOfLines={1}>
                      {diaLargo(f.cuando as Date, idioma)}
                    </Text>
                    <Text fontSize='sm' opacity={0.9} noOfLines={1}>
                      {[
                        horario(f, idioma),
                        !salaComun ? f.sala : '',
                        !personasComun ? personasTexto(f.personas ?? reserva?.numberPeople) : '',
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </Text>
                  </Box>
                  {f.code && (
                    <Badge bg='whiteAlpha.300' color='white' fontSize='0.65em' flexShrink={0}>
                      {f.code}
                    </Badge>
                  )}
                </Flex>
              ))}
            </VStack>
          )}

          {lista.length > 0 && compacta && (
            <VStack align='stretch' spacing={3} data-testid='serie-fechas'>
              {porMes.map((g) => (
                <Box key={g.clave}>
                  <Text
                    fontSize='sm'
                    fontWeight='bold'
                    textTransform='uppercase'
                    letterSpacing='wide'
                    opacity={0.85}
                    mb={1.5}
                  >
                    {g.titulo}
                  </Text>
                  <SimpleGrid columns={columnas} spacing={1.5}>
                    {g.fechas.map((f) => {
                      const detalle = [
                        !horarioComun ? horario(f, idioma) : '',
                        !salaComun ? f.sala : '',
                        !personasComun ? personasTexto(f.personas ?? reserva?.numberPeople) : '',
                      ]
                        .filter(Boolean)
                        .join(' · ');
                      return (
                        <Box key={f.id} bg='whiteAlpha.200' borderRadius='md' px={2.5} py={1.5}>
                          <Text fontSize='sm' fontWeight='semibold' noOfLines={1}>
                            {diaCorto(f.cuando as Date, idioma)}
                          </Text>
                          {detalle && (
                            <Text fontSize='xs' opacity={0.9} noOfLines={2}>
                              {detalle}
                            </Text>
                          )}
                        </Box>
                      );
                    })}
                  </SimpleGrid>
                </Box>
              ))}
            </VStack>
          )}

          {/* Lo que no entra, dicho con claridad. */}
          {/* Si ya se dijo arriba que están todas canceladas, no se repite. */}
          {lista.length > 0 &&
            (canceladas.length > 0 || (porVenir.length > 0 && pasadas.length > 0)) && (
              <VStack
                align='stretch'
                spacing={0.5}
                fontSize='xs'
                opacity={0.85}
                data-testid='serie-notas'
              >
                {porVenir.length > 0 && pasadas.length > 0 && (
                  <Text>
                    {t(
                      pasadas.length === 1
                        ? 'reserve_confirmation.series_past_one'
                        : 'reserve_confirmation.series_past_many',
                      {
                        count: pasadas.length,
                        dates: enumerar(
                          pasadas.map((f) => f.cuando as Date),
                          idioma
                        ),
                      }
                    )}
                  </Text>
                )}
                {canceladas.length > 0 && (
                  <Text>
                    {t(
                      canceladas.length === 1
                        ? 'reserve_confirmation.series_cancelled_one'
                        : 'reserve_confirmation.series_cancelled_many',
                      {
                        count: canceladas.length,
                        dates: enumerar(
                          canceladas.map((f) => f.cuando as Date),
                          idioma
                        ),
                      }
                    )}
                  </Text>
                )}
              </VStack>
            )}

          {lista.length > 0 && codigoPrincipal && (
            <HStack spacing={1.5} fontSize='sm' data-testid='serie-codigo'>
              <Icon as={HashIcon as any} boxSize={4} />
              <Text>{t('reserve_confirmation.series_code_hint', { code: codigoPrincipal })}</Text>
            </HStack>
          )}

          {lista.length > 0 && (
            <Flex gap={3} direction={{ base: 'column', sm: 'row' }} pt={1}>
              <Button
                bg='#25d366'
                color='white'
                size='md'
                _hover={{ bg: 'green.500' }}
                px={6}
                fontWeight='bold'
                borderRadius={35}
                onClick={compartir}
              >
                {t('reserve_confirmation.text_share_whatsapp')}
              </Button>
              {reserva?.id && empresa && (
                <AgregarACalendario
                  reservation={reserva as unknown as Reservation}
                  reservationId={reserva.id}
                  company={empresa}
                  idioma={idioma}
                  botonProps={{
                    variant: 'outline',
                    size: 'md',
                    px: 6,
                    color: 'white',
                    borderColor: 'whiteAlpha.700',
                    borderRadius: 35,
                    fontWeight: 'medium',
                    _hover: { bg: 'whiteAlpha.200', borderColor: 'white' },
                  }}
                />
              )}
            </Flex>
          )}
        </VStack>
      </Box>
    </Flex>
  );
};

const TarjetaSerie = ({ isOpen, onClose, reserva, fechas }: Props) => {
  const { user } = useAuth();
  const empresa = user?.company as Company | undefined;
  return (
    <Modal isOpen={isOpen} onClose={onClose} size='5xl' isCentered scrollBehavior='inside'>
      <ModalOverlay backdropFilter='blur(2px)' />
      <ModalContent bg='transparent' boxShadow='none' data-testid='tarjeta-serie'>
        <ModalCloseButton
          bg='white'
          borderRadius='full'
          boxShadow='md'
          zIndex={2}
          aria-label='Cerrar la tarjeta'
        />
        <ModalBody p={0}>
          <CuerpoTarjetaSerie reserva={reserva} fechas={fechas} empresa={empresa} />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default TarjetaSerie;
